import { useCallback, useMemo, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { UploadSimple, Image as ImageIcon } from "@phosphor-icons/react";
import { slotRegistry, type SlotEditorProps } from "../slot_registry.js";

function ImageSlotEditor({
  content,
  update,
  saveImmediately,
}: SlotEditorProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const prevUrlRef = useRef<string | null>(null);

  const imageUrl = useMemo(() => {
    if (content.length === 0) return null;
    const buffer = content.buffer.slice(
      content.byteOffset,
      content.byteOffset + content.byteLength,
    ) as ArrayBuffer;
    const blob = new Blob([buffer]);
    return URL.createObjectURL(blob);
  }, [content]);

  // Cleanup object URLs
  useEffect(() => {
    if (prevUrlRef.current && prevUrlRef.current !== imageUrl) {
      URL.revokeObjectURL(prevUrlRef.current);
    }
    prevUrlRef.current = imageUrl;

    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const buffer = await file.arrayBuffer();
      const newContent = new Uint8Array(buffer);
      update(newContent);
      saveImmediately(newContent);
    },
    [update, saveImmediately],
  );

  const handleButtonClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <div className="space-y-2">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {imageUrl ? (
        <div className="relative">
          <img
            src={imageUrl}
            alt="Slot content"
            className="max-h-64 w-full rounded object-contain"
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={handleButtonClick}
            className="absolute bottom-2 right-2"
          >
            <UploadSimple size={16} className="mr-1" />
            Replace
          </Button>
        </div>
      ) : (
        <Button
          variant="outline"
          onClick={handleButtonClick}
          className="w-full py-8"
        >
          <ImageIcon size={24} className="mr-2" />
          Upload Image
        </Button>
      )}
    </div>
  );
}

slotRegistry.register({
  type: "image",
  component: ImageSlotEditor,
});
